import {
  BellOutlined,
  DownOutlined,
  LoadingOutlined,
  ReloadOutlined,
  UpOutlined,
} from '@ant-design/icons'
import appStore from '@stores/appStore'
import assistantStore, {
  AssistantBriefingSection,
} from '@stores/assistantStore'
import { observer } from 'mobx-react'
import { ReactNode } from 'react'

import { renderAssistantDates } from './assistantDates'
import { openAssistantRoute } from './assistantNavigation'
import { assistantZoneLabel } from './assistantZoneLabel'
import { IconGhostButton, KW } from './ui'

/** What a section counts, in one and in many, for the one-line summary. */
const SECTION_NOUNS: Record<string, [string, string]> = {
  actions: ['action item', 'action items'],
  appointments: ['IME appointment', 'IME appointments'],
  overdue: ['overdue IME task', 'overdue IME tasks'],
}

const summaryOf = (sections: AssistantBriefingSection[]) =>
  sections
    .map(section => {
      const nouns = SECTION_NOUNS[section.key]
      const count = section.count.toLocaleString('en-AU')
      return nouns
        ? `${count} ${section.count === 1 ? nouns[0] : nouns[1]}`
        : `${section.title}: ${count}`
    })
    .join(' · ')

/** The briefing as one row: the button that asks for it, or its summary once it is loaded. */
const BriefingRow = ({
  children,
  trailing,
  onClick,
  disabled,
  expanded,
}: {
  children: ReactNode
  trailing: ReactNode
  onClick: () => void
  disabled?: boolean
  expanded?: boolean
}) => (
  <button
    type="button"
    disabled={disabled}
    aria-expanded={expanded}
    onClick={onClick}
    className="flex w-full cursor-pointer items-center gap-2 rounded-[10px] px-3 py-[9px] text-left text-[13px] font-semibold leading-[1.35]"
    style={{
      fontFamily: 'inherit',
      color: KW.body,
      background: KW.soft,
      border: `1px solid ${KW.softBorder}`,
      cursor: disabled ? 'default' : undefined,
    }}
  >
    <BellOutlined className="shrink-0" style={{ color: KW.teal500 }} />
    <span className="min-w-0 flex-1">{children}</span>
    <span className="shrink-0 text-[11px]" style={{ color: KW.muted }}>
      {trailing}
    </span>
  </button>
)

/*
 * "Today" — what needs the user's attention, from the server's deterministic briefing
 * (action items overdue/due today, appointments, overdue tasks). It is loaded when the user
 * asks for it, and then shown in full or as one line of counts. Items open the screen; a
 * section's prompt hands the question to the assistant.
 */
const BriefingCard = () => {
  const { briefing, briefingLoading, briefingFailed, briefingExpanded } =
    assistantStore

  if (!briefing) {
    return (
      <div className="mb-3">
        <BriefingRow
          onClick={() => assistantStore.showBriefing()}
          disabled={briefingLoading}
          trailing={briefingLoading ? <LoadingOutlined /> : <DownOutlined />}
        >
          {briefingLoading
            ? 'Checking what needs your attention…'
            : 'See what needs your attention today'}
        </BriefingRow>
        {briefingFailed && !briefingLoading && (
          <div
            role="status"
            className="mt-1 text-xs"
            style={{ color: KW.amber }}
          >
            Couldn&apos;t load it. Please try again.
          </div>
        )}
      </div>
    )
  }

  const dateFormat = appStore.currentUser?.dateFormat
  const defaultTimeZone = appStore.timezone
  const sections = briefing.sections.filter(s => s.count > 0)

  if (!briefingExpanded) {
    return (
      <div className="mb-3">
        <BriefingRow
          expanded={false}
          onClick={() => assistantStore.showBriefing()}
          trailing={<DownOutlined />}
        >
          {sections.length > 0
            ? `Today: ${summaryOf(sections)}`
            : "Today: you're all caught up"}
        </BriefingRow>
      </div>
    )
  }

  return (
    <div
      className="mb-5 rounded-[12px] px-[14px] py-3"
      style={{ border: `1px solid ${KW.line2}`, background: '#fff' }}
      aria-label="Today's briefing"
    >
      <div className="mb-2 flex items-center justify-between">
        <div
          className="text-[11px] font-extrabold uppercase tracking-widest"
          style={{ color: KW.muted }}
        >
          Needs your attention today
        </div>
        <div className="flex items-center">
          <IconGhostButton
            title="Refresh"
            icon={<ReloadOutlined spin={briefingLoading} />}
            onClick={() => assistantStore.loadBriefing(true)}
          />
          <IconGhostButton
            title="Collapse"
            icon={<UpOutlined />}
            onClick={() => assistantStore.collapseBriefing()}
          />
        </div>
      </div>

      {briefingFailed && !briefingLoading && (
        <div role="status" className="mb-2 text-xs" style={{ color: KW.amber }}>
          Couldn&apos;t refresh. Please try again.
        </div>
      )}

      {sections.length === 0 && (
        <div className="text-[13px]" style={{ color: KW.body }}>
          You&apos;re all caught up — nothing overdue or due today.
        </div>
      )}

      {sections.map(section => (
        <div key={section.key} className="mb-3 last:mb-0">
          <div className="mb-1 text-[13px] font-bold" style={{ color: KW.ink }}>
            {section.title}{' '}
            <span style={{ color: KW.muted }}>({section.count})</span>
          </div>
          <ul className="m-0 list-none p-0">
            {section.items.map((item, index) => (
              <li
                key={`${section.key}-${index}`}
                className="flex items-baseline justify-between gap-3 py-[2px] text-[12.5px]"
              >
                {item.route ? (
                  <button
                    type="button"
                    className="cursor-pointer border-0 bg-transparent p-0 text-left font-semibold hover:underline"
                    style={{ color: KW.teal500, fontFamily: 'inherit' }}
                    onClick={() =>
                      openAssistantRoute(item.route, item.query, item.tabTitle)
                    }
                  >
                    {item.label}
                  </button>
                ) : (
                  <span style={{ color: KW.body }}>{item.label}</span>
                )}
                <span
                  className="shrink-0"
                  style={{ color: item.overdue ? '#c2410c' : KW.muted }}
                >
                  {renderAssistantDates(item.detail ?? '', {
                    dateFormat,
                    defaultTimeZone,
                    zoneLabel: assistantZoneLabel,
                  })}
                </span>
              </li>
            ))}
          </ul>
          {section.askPrompt && (
            <button
              type="button"
              className="mt-1 cursor-pointer border-0 bg-transparent p-0 text-xs font-bold"
              style={{ color: KW.teal500, fontFamily: 'inherit' }}
              disabled={assistantStore.isInputLocked}
              onClick={() => assistantStore.sendMessage(section.askPrompt)}
            >
              Ask: {section.askPrompt} →
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

export default observer(BriefingCard)
